import React, { useCallback, useEffect, useState } from "react";
import { Upload, Check, Trash2, Image as ImageIcon, Stamp } from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";
import {
  getSignatureAssets,
  uploadSignatureAsset,
  activateSignatureAsset,
  deleteSignatureAsset,
} from "../../../api/settings/signatureAssets";
import { showErrorToast, showSuccessToast, showConfirmDialog } from "../../../components/ConfirmDialog";

const GROUPS = [
  { owner_role: "COO", asset_type: "signature", title: "Chữ ký COO", icon: Stamp },
  { owner_role: "CEO", asset_type: "signature", title: "Chữ ký CEO", icon: Stamp },
  { owner_role: "COMPANY", asset_type: "stamp", title: "Con dấu công ty", icon: Stamp },
];

const AssetCard = ({ asset, onRefresh }) => {
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await axiosInstance.get(`/settings/signature-assets/${asset.id}/image`, {
          responseType: "blob",
        });
        objectUrl = URL.createObjectURL(res.data);
        if (!cancelled) setImgUrl(objectUrl);
      } catch {
        if (!cancelled) setImgUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, asset.updated_at]);

  const handleActivate = async () => {
    try {
      await activateSignatureAsset(asset.id);
      showSuccessToast("Đã kích hoạt.");
      onRefresh();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi kích hoạt.");
    }
  };

  const handleDelete = async () => {
    const result = await showConfirmDialog({
      title: "Xóa asset này?",
      text: "Hành động không thể hoàn tác.",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteSignatureAsset(asset.id);
      showSuccessToast("Đã xóa.");
      onRefresh();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi xóa.");
    }
  };

  return (
    <div
      className={`border rounded-2xl p-4 flex flex-col gap-3 transition-all ${
        asset.is_active
          ? "border-emerald-300 bg-emerald-50/40 shadow-sm"
          : "border-gray-100 bg-white hover:border-red-200"
      }`}
    >
      <div className="h-24 flex items-center justify-center bg-white rounded-xl border border-gray-100 overflow-hidden">
        {imgUrl ? (
          <img src={imgUrl} alt={asset.label} className="max-h-full max-w-full object-contain" />
        ) : (
          <ImageIcon className="text-gray-300" size={32} />
        )}
      </div>
      <p className="text-xs font-bold text-gray-700 truncate">{asset.label}</p>
      {asset.is_active && (
        <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider">
          Đang dùng
        </span>
      )}
      <div className="flex gap-2 mt-auto">
        {!asset.is_active && (
          <button
            type="button"
            onClick={handleActivate}
            className="flex-1 flex items-center justify-center gap-1 text-xs py-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 text-white font-bold uppercase tracking-wider hover:shadow-md transition-all"
          >
            <Check size={12} /> Kích hoạt
          </button>
        )}
        <button
          type="button"
          onClick={handleDelete}
          className="p-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
          title="Xóa"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const UploadBox = ({ group, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState(group.title);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/png") {
      showErrorToast("Chỉ chấp nhận file PNG.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_role", group.owner_role);
      fd.append("asset_type", group.asset_type);
      fd.append("label", label || group.title);
      await uploadSignatureAsset(fd);
      showSuccessToast("Upload thành công.");
      onUploaded();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi upload.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 flex flex-col gap-3 hover:border-red-300 transition-colors">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Tên hiển thị"
        className="text-xs border border-gray-200 rounded-xl px-3 py-2 font-medium focus:border-red-500 outline-none"
      />
      <label className="flex flex-col items-center gap-2 cursor-pointer py-4 text-gray-500 hover:text-red-600 transition-colors">
        <Upload size={24} />
        <span className="text-xs font-bold uppercase tracking-wider">
          {uploading ? "Đang upload..." : "Chọn PNG"}
        </span>
        <input type="file" accept="image/png" className="hidden" onChange={handleFile} disabled={uploading} />
      </label>
    </div>
  );
};

export default function SignatureAssetListPage() {
  const [assets, setAssets] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await getSignatureAssets();
      setAssets(res.records || []);
    } catch {
      showErrorToast("Không tải được danh sách chữ ký.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8 max-w-6xl mx-auto">
      <div className="mb-6 flex items-center gap-3 sm:gap-4">
        <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
          <Stamp className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div>
          <h1 className="text-xl font-black uppercase tracking-tight text-gray-800 sm:text-2xl">
            Quản lý <span className="text-red-700">Chữ ký & Con dấu</span>
          </h1>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:tracking-[0.2em]">
            Upload PNG nền trong suốt — mỗi nhóm một bản active
          </p>
        </div>
      </div>

      <div className="grid gap-8 md:grid-cols-3">
        {GROUPS.map((group) => {
          const groupAssets = assets.filter(
            (a) => a.owner_role === group.owner_role && a.asset_type === group.asset_type
          );
          return (
            <div
              key={`${group.owner_role}-${group.asset_type}`}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
            >
              <h2 className="text-sm font-black uppercase tracking-tight text-gray-800 mb-4 flex items-center gap-2">
                <group.icon size={16} className="text-red-700" />
                {group.title}
              </h2>
              <div className="grid gap-3">
                <UploadBox group={group} onUploaded={load} />
                {groupAssets.map((asset) => (
                  <AssetCard key={asset.id} asset={asset} onRefresh={load} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
